import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A table's rows as cards, for a phone (spec 263).
 *
 * A table wider than the screen scrolls sideways, and on a phone that hides
 * every column after the second. The table stays the desktop's: its page
 * renders this list instead below 768px, from the same rows, so nothing about
 * the desktop's markup changes.
 */
export function PhoneCardList({
  label,
  className,
  ...props
}: ComponentProps<'ul'> & { label: string }) {
  return (
    <ul
      aria-label={label}
      className={cn('flex min-w-0 flex-col gap-2', className)}
      {...props}
    />
  );
}

/**
 * One row as a card: a title that opens the row, what sits beside it, the
 * facts under it, and its actions in the top corner.
 *
 * The title is the card's one link to its row, stretched over the whole card,
 * so a tap anywhere opens it; the actions sit above that stretch and answer for
 * themselves. With no `onOpen` the card opens nothing.
 */
export function PhoneCard({
  title,
  badges,
  lines,
  actions,
  footer,
  children,
  onOpen,
  selected = false,
  className,
  ...props
}: Omit<ComponentProps<'li'>, 'title'> & {
  title: ReactNode;
  /** Beside the title: a status, a Default, a "(deleted)". */
  badges?: ReactNode;
  /** The facts under the title, one line each; empty ones are skipped. */
  lines?: readonly ReactNode[];
  /** Top right: a ⋯ that opens an action sheet. */
  actions?: ReactNode;
  /** Below the facts, above the stretch: a switch, a "Set as default". */
  footer?: ReactNode;
  onOpen?: () => void;
  selected?: boolean;
}) {
  const shown = (lines ?? []).filter(
    (line) =>
      line !== null && line !== undefined && line !== false && line !== '',
  );
  return (
    <li
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'border-border bg-card relative flex min-w-0 flex-col gap-1.5 rounded-lg border p-4',
        selected && 'bg-selected',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className="text-foreground focus-visible:ring-ring min-w-0 text-left text-base font-medium [overflow-wrap:anywhere] after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-3"
            >
              {title}
            </button>
          ) : (
            <span className="text-foreground min-w-0 text-base font-medium [overflow-wrap:anywhere]">
              {title}
            </span>
          )}
          {badges}
        </div>
        {actions ? (
          <div className="relative z-10 -mt-2 -mr-2 shrink-0">{actions}</div>
        ) : null}
      </div>
      {shown.map((line, index) => (
        <div
          key={index}
          className="text-muted-foreground min-w-0 text-sm [overflow-wrap:anywhere]"
        >
          {line}
        </div>
      ))}
      {footer ? <div className="relative z-10 pt-1">{footer}</div> : null}
      {children}
    </li>
  );
}
